import type { LangText } from "../lib/resolve-text";
import type { ConditionGroup } from "../lib/evaluate-condition";

export enum DataType {
  ID = "ID",
  OBJECT_ID = "OBJECT_ID",
  GUEST_STATUS = "GUEST_STATUS",
  CTA = "CTA",
  ACTION = "ACTION",
  STRING = "STRING",
  LONG = "LONG",
  DOUBLE = "DOUBLE",
  CURRENCY = "CURRENCY",
  BOOLEAN = "BOOLEAN",
  EMAIL = "EMAIL",
  URL = "URL",
  DATE = "DATE",
  DATE_TIME = "DATE_TIME",
  TIME = "TIME",
  SINGLE_SELECT = "SINGLE_SELECT",
  MULTI_SELECT = "MULTI_SELECT",
  /** Guest image additional field. The public form never writes the stored
   *  {key, contentType, size} value directly — it uploads the file to
   *  POST /api/runtime/af-image-upload and submits the returned pending id
   *  as `{__pendingUpload: <id>}` (see formInput's ImageUploadInput). */
  IMAGE = "IMAGE",
  COMPANIONS = "COMPANIONS",
  EMAIL_STATUS = "EMAIL_STATUS",
  LIMITS = "LIMITS",
  EVENT_LIMIT_META = "EVENT_LIMIT_META",
  MAIN_GUEST = "MAIN_GUEST",
  UNKNOWN = "UNKNOWN",
}

export interface Field {
  fieldId?: string;
  name: string;
  translation?: string;
  dataType: DataType;
  additionalField: boolean;
  required?: boolean;
  manualCheck?: boolean;
  defaultValue?: unknown;
  options?: string[];
  lowerBound?: number | Date;
  upperBound?: number | Date;
  /**
   * Conditional-display rules set by the event owner in the admin builder and
   * stored on the field definition. Evaluated live against the guest's current
   * form values via {@link evaluateConditionGroup}:
   *   - {@link visibilityCondition}: unmet → the field is hidden + its value
   *     cleared on submit (website-builder-backend strips it server-side too).
   *   - {@link editableCondition}: unmet → the field is shown but locked.
   *   - {@link optionConditions}: per-option (keyed by label) — an option is
   *     selectable only when its condition is met (dependent dropdowns).
   * ConditionGroup wire shape: `{ operator, filters[], groups[] }`.
   */
  visibilityCondition?: ConditionGroup;
  editableCondition?: ConditionGroup;
  optionConditions?: Record<string, ConditionGroup>;
  editable: boolean;
}

export interface Page {
  pageId?: string;
  eventId?: string;
  pageName?: string;
  slug?: string;
  active?: boolean;
  websiteJson?: string;
}

export interface WebsiteConfig {
  websiteConfigId?: string;
  eventId?: string;
  bodyFont?: string;
  headerFont?: string;
  primaryColor?: string;
  secondaryColor?: string;
  backgroundColor?: string;
  rounding?: string;
  appBar?: string;
  footer?: string;
  homeSlug?: string;
  /** Primary (default) content language as a full BCP-47 locale (e.g. "de-DE"). */
  primaryLanguage?: string;
  /** Additional content languages beyond primaryLanguage (excludes it). */
  languages?: string[];
  published?: boolean;
  favIcon?: string;
  touchIcon?: string;
  colorCookieHeader?: string;
  fontCookieHeader?: string;
  colorCookieBody?: string;
  fontCookieBody?: string;
  backgroundCookieColor?: string;
  cookieHeaderText?: LangText;
  cookieBodyText?: LangText;
  cookieEnabled?: boolean;
  cookiePolicyUrl?: string;
  /** Operator's own pages for the legal line; absent ⇒ the platform's. */
  imprintUrl?: string;
  privacyPolicyUrl?: string;
  colorCookieButton?: string;
  colorCookieButtonText?: string;
  colorCookieLink?: string;
  customDomain?: string;
  cloudfrontDomain?: string;
  siteTheoreticallyOnCustomDomain?: boolean;
  siteTheoreticallyCustomSSL?: boolean;

  bodyFontColor?: string;
  bodyFontSize?: string;
  bodyLayoutOption?: string;

  h1FontFamily?: string;
  h1FontSize?: string;
  h1FontColor?: string;
  h2FontFamily?: string;
  h2FontSize?: string;
  h2FontColor?: string;
  h3FontFamily?: string;
  h3FontSize?: string;
  h3FontColor?: string;

  primaryBtnBgColor?: string;
  primaryBtnBgHoverColor?: string;
  primaryBtnBorderColor?: string;
  primaryBtnBorderHoverColor?: string;
  primaryBtnFontColor?: string;
  primaryBtnFontHoverColor?: string;
  primaryBtnFontSize?: string;
  primaryBtnFontHoverSize?: string;
  primaryBtnBorderRadius?: string;
  primaryBtnHoverBorderRadius?: string;

  secondaryBtnBgColor?: string;
  secondaryBtnBgHoverColor?: string;
  secondaryBtnBorderColor?: string;
  secondaryBtnBorderHoverColor?: string;
  secondaryBtnFontColor?: string;
  secondaryBtnFontHoverColor?: string;
  secondaryBtnFontSize?: string;
  secondaryBtnFontHoverSize?: string;
  secondaryBtnBorderRadius?: string;
  secondaryBtnHoverBorderRadius?: string;

  hyperlinkFontColor?: string;
  hyperlinkFontHoverColor?: string;
  hyperlinkUnderline?: boolean;

  formInputLabelFontColor?: string;
  formInputInputFontColor?: string;
  formInputBackgroundColor?: string;
  formInputBorder?: boolean;
  formInputBorderColor?: string;
  formInputBorderRadius?: string;
  formInputBorderStyle?: string;
  formInputBorderWidth?: string;
  formInputBorderSides?: string[];
  formInputFontFamily?: string;
  formInputFontSize?: string;
  formInputErrorColor?: string;

  openGraphTitle?: LangText;
  openGraphDescription?: LangText;
  openGraphImageUrl?: string;
  customCss?: string;
  defaultLoginJson?: string;
  defaultRegisterJson?: string;
  senderEmail?: string;
  loginAfterRegister?: boolean;
  notifyEmail?: string;

  loginBlockEnabled?: boolean;
  /**
   * M35: DERIVED — "the manual lockdown carries an exemption rule". Never the
   * rule itself: `loginBlockConditions` stays server-side, because shipping it
   * would hand every anonymous visitor the organizer's segmentation. Without
   * this bit a lockdown with exemptions renders as a full lockdown and the
   * exempt guest never reaches the endpoint that would accept them.
   */
  loginBlockHasExemptions?: boolean;
  /** Derived: at least one per-segment registration window with a bound
   *  exists. Says THAT a per-guest schedule rule exists, never who for. */
  scheduleHasSegmentWindows?: boolean;
  loginBlockMessage?: LangText;
  availableFrom?: string;
  availableUntil?: string;
}

export type CraftNode = {
  type?: { resolvedName?: string };
  props?: Record<string, unknown>;
  nodes?: string[];
  linkedNodes?: Record<string, string>;
  displayName?: string;
  hidden?: boolean;
  [key: string]: unknown;
};

export type CraftNodeValue = CraftNode | string;
export type CraftData = Record<string, CraftNodeValue>;

export type RuntimeUser = {
  userId?: string;
  inviteeId?: string;
  guestId?: string;
  mainGuest?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  company?: string;
  jobTitle?: string;
  title?: string;
  language?: string;
  city?: string;
  country?: string;
  zipCode?: string;
  address?: string;
  address2?: string;
  username?: string;
  code?: string;
  emailOptIn?: boolean;
  state?: string;
  acceptanceState?: string;
  additionalFields?: Record<string, unknown>;
  companions?: RuntimeUser[];
  [key: string]: unknown;
};

export type RuntimeSession = {
  authenticated?: boolean;
  userId?: string;
  email?: string;
  user?: RuntimeUser;
  [key: string]: unknown;
} | null;
